import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Badge,
  useColorMode,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import {
  Star,
  Calendar,
  CalendarCheck,
  Clock,
  ShieldCheck,
  Languages,
  MapPin,
  ListChecks,
  ArrowRight,
  Download,
  ExternalLink,
  CheckCircle2,
  Wallet,
  Gift,
  Lock,
  Flame,
  Sparkles,
  Zap,
  Terminal,
  Monitor,
  Database,
  Briefcase,
  Bot,
  Globe,
  Users,
  Cpu,
  Rocket,
} from "lucide-react";
import { FaJava, FaPython } from "react-icons/fa";
import { SiFastapi, SiSpring } from "react-icons/si";
import { FiLayers } from "react-icons/fi";

/* Existing project assets — reused as-is */
import pythonbe from "../../assets/images/Complete Python Backend Development.png";
import pythonfe from "../../assets/images/Complete Full Stack Development in Python.png";
import sapsd from "../../assets/images/Complete SAP SD Telugu Course.png";
import frontend from "../../assets/images/Telugu Frontend Development Course.png";
import cpp from "../../assets/images/Complete C++ Course in Telugu.png";
import lld from "../../assets/images/LLD.png";
import javabe from "../../assets/images/BackendJava.png";
import freepython from "../../assets/images/freepython.png";
/* Hero banner artwork (kept) */
import heroBanner from "../../assets/images/Falcon Knight AI Engineering Course Poster.png";
import spring from "../../assets/images/Complete Spring Boot Telugu Course.png";
import dataeng from "../../assets/images/Complete Data Engineer Course in Telugu.png";
import aieng from "../../assets/images/AI Engineer Telugu Course Thumbnail.png";
import falcon from "../../assets/images/Falcon AI Engineer Course Promo.png";

/* ══════════════════════════════════════════════════════════════════════════
   God Level Courses — Bootcamps page

   Layout:
     1. Hero banner (image kept, festival copy removed)
     2. "Fresh Drops" special section — Complete Python Developer Live Course
        (free, Google Form enrollment), Complete Data Engineering Course,
        Complete AI Engineer Course, the Falcon combo (AI + Data Engineer)
        and the Spring Boot Masterclass
     3. All other courses — enroll button disabled (ENROLLMENT_OPEN = false)

   A course is featured with `featured: true`. A course's enroll area is live when:
     • it has an `enrollLink` (external form, e.g. Google Form) and
       either `enrollOpen: true` or the global ENROLLMENT_OPEN switch is on, or
     • it has a `paymentButtonId` (Razorpay) and the same condition holds.
   ══════════════════════════════════════════════════════════════════════════ */

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;
const DISPLAY = "'Bricolage Grotesque', sans-serif";
const BODY = "'Inter', sans-serif";
const MONO = "'JetBrains Mono', monospace";

/* ─── Token system ─────────────────────────────────────────────────────── */
const INK      = "#15140F";
const PAPER    = "#FBF6EA";
const GREEN    = "#0F4C3A";
const GREEN_LT = "#1D8C67";
const GOLD     = "#FC0304";
const CORAL    = "#C0392B";

function useTheme(isDark) {
  return {
    bg: isDark ? INK : PAPER,
    surfaceSolid: isDark ? "#1C1B15" : "#FFFFFF",
    cardBg: isDark ? "#1F1E17" : "#FFFDF6",
    border: isDark ? "rgba(255,255,255,0.10)" : "rgba(21,20,15,0.12)",
    text: isDark ? "rgba(255,255,255,0.95)" : INK,
    muted: isDark ? "rgba(255,255,255,0.55)" : "#5B5748",
    faint: isDark ? "rgba(255,255,255,0.32)" : "#9C9686",
  };
}

/* ─── Global enrollment switch (applies to every course that doesn't set
   its own `enrollOpen: true`) ───────────────────────────────────────── */
const ENROLLMENT_OPEN = false;

/* ─── Razorpay payment button ids ─────────────────────────────────────── */
const RAZORPAY_PAYMENT_BUTTON_ID_BACKEND_JAVA = "pl_SbcM9Nb3CAhqA7";
const RAZORPAY_PAYMENT_BUTTON_ID_BACKEND_PYTHON = "pl_TQiId7VvLoDyFS";
const RAZORPAY_PAYMENT_BUTTON_ID_LLD = "pl_TAEuPLhjuK9jiL";
const RAZORPAY_PAYMENT_BUTTON_ID_SPRING_BOOT = "pl_SbcM9Nb3CAhqA7";
const RAZORPAY_PAYMENT_BUTTON_ID_DATA_ENGINEERING = "pl_SbcEDdqCyHz4iD";
/* AI Engineer + Falcon combo reuse the Data Engineering payment link */
const RAZORPAY_PAYMENT_BUTTON_ID_AI_ENGINEER = "pl_TQiId7VvLoDyFS";
const RAZORPAY_PAYMENT_BUTTON_ID_FALCON = "pl_TGymw0kukirwcj";

/* ─── Google Form enrollment links ─────────────────────────────────────── */
const GOOGLE_FORM_PYTHON_DEVELOPER =
  "https://docs.google.com/forms/d/e/1FAIpQLScYwO8hYqQLyX4Ar4isKDCHvTkGGdNuNerWZxH9uheO6gdqLA/viewform?usp=publish-editor";

function RazorpayPaymentButton({ buttonId }) {
  const formRef = useRef(null);
  useEffect(() => {
    const form = formRef.current;
    if (!form || !buttonId) return;
    form.innerHTML = "";
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/payment-button.js";
    script.async = true;
    script.setAttribute("data-payment_button_id", buttonId);
    form.appendChild(script);
  }, [buttonId]);
  return <form ref={formRef} className="glc-rzp-form" />;
}

const COHORT_START_DATE = "September 15, 2026";
const STANDARD_PRICE = 5999;

/* ─── Shared career-prep track ─────────────────────────────────────────── */
const CAREER_TRACK = {
  key: "career",
  label: "Career Prep",
  color: GOLD,
  phases: ["Resume Preparation", "Mock & Technical Interviews", "HR Interview Prep"],
};

/* ─── Shared curriculum tracks (Data Engineering + AI Engineer) so the
   standalone courses and the Falcon combo always stay in sync ────────── */
const DE_FOUNDATIONS_TRACK = {
  key: "de-foundations",
  label: "Foundations",
  color: GREEN,
  phases: ["Python for Data Engineering", "Advanced SQL", "Data Modeling & Warehousing"],
};

const DE_PIPELINES_TRACK = {
  key: "de-pipelines",
  label: "Pipelines & Big Data",
  color: CORAL,
  phases: ["ETL / ELT Pipelines", "Apache Spark", "Workflow Orchestration (Airflow)", "Cloud Data Platforms"],
};

const AI_FOUNDATIONS_TRACK = {
  key: "ai-foundations",
  label: "AI & ML Foundations",
  color: GREEN,
  phases: [
    "Python, NumPy & Pandas for AI",
    "Math & Statistics Essentials",
    "Machine Learning with scikit-learn",
    "Deep Learning with PyTorch",
  ],
};

const AI_GENAI_TRACK = {
  key: "ai-genai",
  label: "Generative AI & LLMs",
  color: CORAL,
  phases: [
    "NLP & Transformers",
    "Prompt Engineering",
    "RAG & Vector Databases",
    "LangChain & AI Agents",
    "Building & Deploying AI Apps (FastAPI, Docker)",
    "AI Capstone Project",
  ],
};

/* ─── Course catalog ─────────────────────────────────────────────────────
   `image: null` renders a gradient + icon placeholder. `originalPrice` /
   `duration` / `endDate` / `mode` / `seats` are optional. `price: 0` renders
   as "Free". `reviews` are optional (omit for brand-new courses).
   ────────────────────────────────────────────────────────────────────── */
const COURSES = [
  /* ───────────── Fresh drops (featured section, live enrollment) ───────────── */
  
  {
    id: "python-developer-live",
    featured: true,
    fullImage: true,
    enrollOpen: true,
    spotlight: true,
    badge: "🎁 Free Live Course",
    icon: FaPython,
    image: freepython, // placeholder — replace with the new course artwork
    gradient: ["#0F4C3A", "#1D8C67"],
    title: "Complete Python Developer Live Course",
    description:
      "Start from zero and go all the way to OOPs in a free, live, online course by GrapplTech. Write real Python, understand how it works, and build the foundation every backend, data and AI career sits on.",
    language: "Telugu",
    mode: "Online (Live)",
    startDate: "Nov 10, 2026",
    endDate: "Nov 15, 2026",
    seats: 100,
    price: 0,
    paymentButtonId: null,
    enrollLink: GOOGLE_FORM_PYTHON_DEVELOPER,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "py-basics",
        label: "Python Basics",
        color: GREEN,
        phases: [
          "Introduction & Environment Setup",
          "Variables, Data Types & Operators",
          "Conditionals & Loops",
          "Functions & Modules",
          "Strings, Lists, Tuples, Sets & Dictionaries",
          "File Handling & Exception Handling",
        ],
      },
      {
        key: "py-oops",
        label: "Object-Oriented Programming (OOPs)",
        color: CORAL,
        phases: [
          "Classes & Objects",
          "Constructors & Instance/Class Variables",
          "Inheritance",
          "Polymorphism",
          "Encapsulation & Abstraction",
        ],
      },
    ],
  },
  {
    id: "falcon-combo",
    featured: true,
    fullImage: true,
    enrollOpen: true,
    spotlight: true,
    badge: "🦅 Best Combo",
    icon: Rocket,
    image: falcon, // same artwork as Data Engineering for now
    gradient: ["#0F4C3A", "#15140F"],
    title: "Falcon Combo — AI Engineer + Data Engineer Live Course",
    description:
      "Two of the hottest skills in tech, one bundle, 2 months. Learn to build the data pipelines AND the AI on top of them — the full stack of the AI era at a price that's honestly criminal.",
    language: "Telugu",
    duration: "2 Months",
    startDate: "December 01, 2026",
    price: 3599,
    originalPrice: 4498,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_FALCON,
    curriculumLink: null,
    bundleNote:
      "Combo drop: AI Engineer + Data Engineer together for just ₹3,599. Two careers' worth of skills, one checkout.",
    curriculumTracks: [
      AI_FOUNDATIONS_TRACK,
      AI_GENAI_TRACK,
      { ...DE_FOUNDATIONS_TRACK, key: "falcon-de-foundations", label: "Data Engineering Foundations" },
      { ...DE_PIPELINES_TRACK, key: "falcon-de-pipelines" },
      CAREER_TRACK,
    ],
  },
  {
    id: "data-engineering",
    featured: true,
    fullImage: true,
    enrollOpen: true,
    spotlight: true,
    badge: "🏆 Best in AI Era",
    icon: Database,
    image: dataeng,
    gradient: ["#0F4C3A", "#15140F"],
    title: "Complete Data Engineering Live Course",
    description:
      "AI is only as smart as the data feeding it, and someone has to build those pipelines. Learn Python, SQL, Spark, Airflow and cloud data stacks from the ground up — the skills every AI team is fighting to hire.",
    rating: 4.7,
    reviews: 234,
    language: "Telugu",
    startDate: "December 01, 2026",
    price: 1999,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_DATA_ENGINEERING,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [DE_FOUNDATIONS_TRACK, DE_PIPELINES_TRACK, CAREER_TRACK],
  },
  {
    id: "ai-engineer",
    featured: true,
    fullImage: true,
    enrollOpen: true,
    spotlight: true,
    badge: "🤖 Brand New",
    icon: Cpu,
    image: aieng, // same artwork as Data Engineering for now
    gradient: ["#0F4C3A", "#15140F"],
    title: "Complete AI Engineer Live Course",
    description:
      "Go from Python to production-grade AI in just 2 months. ML, deep learning, LLMs, RAG and AI agents — build real AI apps and become the person every team wants on their AI squad.",
    language: "Telugu",
    duration: "2 Months",
    startDate: "December 01, 2026",
    price: 2499,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_AI_ENGINEER,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [AI_FOUNDATIONS_TRACK, AI_GENAI_TRACK, CAREER_TRACK],
  },
  
  {
    id: "spring-boot",
    featured: true,
    fullImage: true,
    enrollOpen: true,
    badge: "🍃 New Batch",
    icon: SiSpring,
    image: spring,
    title: "Spring Boot Masterclass Live Course",
    description:
      "Go deep on Spring Boot — build, secure and ship production-grade REST services with the patterns real backend teams actually use. Small price, big glow-up.",
    rating: 4.8,
    reviews: 402,
    language: "Telugu",
    startDate: "Nov 11, 2026",
    price: 599,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_SPRING_BOOT,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "boot-core",
        label: "Spring Boot Essentials",
        color: GREEN,
        phases: ["Project Setup & Auto-Configuration", "REST Controllers & Validation", "Spring Data JPA"],
      },
      {
        key: "boot-prod",
        label: "Production-Ready Boot",
        color: CORAL,
        phases: ["Spring Security & JWT", "Exception Handling & Logging", "Testing with JUnit & Mockito", "Docker & Deployment"],
      },
    ],
  },

  /* ───────────── Main catalog (enroll disabled) ───────────── */
  {
    id: "backend-java",
    badge: "🔥 Trending",
    icon: FaJava,
    image: javabe,
    title: "GOD Level Backend Development in Java(Live + Recordings)",
    description:
      "Core Java + the full Spring stack, zero fluff. Build and secure real REST APIs from scratch and walk out with a backend brain that actually ships — no frontend detour, no cap.",
    rating: 4.7,
    reviews: 812,
    language: "Telugu",
    duration: "2 Months",
    startDate: COHORT_START_DATE,
    price: STANDARD_PRICE,
    originalPrice: 9999,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_BACKEND_JAVA,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "java-core",
        label: "Java Core Foundations",
        color: GREEN,
        phases: [
          "Core Java — OOP, Collections & Exceptions",
          "JDBC — Database Connectivity",
          "Maven — Build & Dependency Management",
          "Java Capstone Project",
        ],
      },
      {
        key: "spring-backend",
        label: "Spring & Enterprise Backend",
        color: CORAL,
        phases: [
          "Spring Core — IoC & Dependency Injection",
          "Spring MVC",
          "Spring AOP",
          "Spring REST — Building APIs",
          "Spring Data JPA",
          "Spring Security — Auth & Authorization",
        ],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "backend-python",
    badge: "⚡ Fast-Track",
    icon: SiFastapi,
    image: pythonbe,
    title: "GOD Level Backend Development in Python(Live + Recordings)",
    description:
      "Python basics straight into FastAPI. Ship async, typed, production-ready REST APIs with real auth and real validation — the backend flex your resume's been missing.",
    rating: 4.7,
    reviews: 534,
    language: "Telugu",
    duration: "2 Months",
    startDate: COHORT_START_DATE,
    price: STANDARD_PRICE,
    originalPrice: 9999,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_BACKEND_PYTHON,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "python-core",
        label: "Python Core Foundations",
        color: GREEN,
        phases: [
          "Python Syntax, Data Types & Control Flow",
          "Functions, Modules & OOP in Python",
          "Working with Files, JSON & Exceptions",
          "Virtual Envs & Package Management (pip/poetry)",
        ],
      },
      {
        key: "fastapi-backend",
        label: "FastAPI & Enterprise Backend",
        color: CORAL,
        phases: [
          "FastAPI Fundamentals — Routing & Path/Query Params",
          "Pydantic — Request/Response Validation",
          "Async Endpoints & Dependency Injection",
          "Databases with SQLAlchemy / SQLModel",
          "Authentication & Authorization (JWT/OAuth2)",
          "Testing APIs with Pytest",
          "Deployment — Uvicorn, Docker & CI Basics",
        ],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "lld",
    badge: "🧠 Interview Prep",
    icon: FiLayers,
    image: lld,
    title: "GOD Level Low Level Design(Live + Recordings)",
    description:
      "SOLID principles, design patterns, and real machine-coding rounds. Turn your 'it depends' answers into confident, structured designs before the interviewer even finishes the question.",
    rating: 4.8,
    reviews: 776,
    language: "Telugu",
    duration: "6 Weeks",
    startDate: COHORT_START_DATE,
    price: STANDARD_PRICE,
    originalPrice: 7999,
    paymentButtonId: RAZORPAY_PAYMENT_BUTTON_ID_LLD,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "oop-foundations",
        label: "OOP & Design Foundations",
        color: GREEN,
        phases: ["Object-Oriented Design Principles", "SOLID Principles", "UML & Class Diagrams"],
      },
      {
        key: "patterns",
        label: "Design Patterns",
        color: CORAL,
        phases: ["Creational Patterns", "Structural Patterns", "Behavioral Patterns"],
      },
      {
        key: "case-studies",
        label: "Real-World System Design",
        color: GOLD,
        phases: [
          "Design a Parking Lot System",
          "Design a Library Management System",
          "Design an Elevator System",
          "Mock LLD Interviews",
        ],
      },
    ],
  },
  {
    id: "cpp",
    badge: "💪 Core Skills",
    icon: Terminal,
    image: cpp,
    gradient: ["#0F4C3A", "#1D8C67"],
    title: "Complete C++ Development in Telugu",
    description:
      "From first program to STL and modern C++. Build rock-solid fundamentals in memory, OOP and data structures that carry into every language you learn next.",
    rating: 4.7,
    reviews: 689,
    language: "Telugu",
    startDate: "Soon",
    price: 1999,
    paymentButtonId: null,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "cpp-core",
        label: "C++ Foundations",
        color: GREEN,
        phases: [
          "Syntax, Data Types & Control Flow",
          "Functions, Arrays & Strings",
          "Pointers & Memory Management",
          "OOP — Classes, Inheritance & Polymorphism",
        ],
      },
      {
        key: "cpp-advanced",
        label: "Advanced C++",
        color: CORAL,
        phases: [
          "STL — Containers, Iterators & Algorithms",
          "Templates & Generic Programming",
          "File Handling & Exceptions",
          "Modern C++ (C++11/14/17)",
        ],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "fullstack-java",
    badge: "🚀 Full Stack",
    icon: FaJava,
    image: javabe,
    title: "Full Stack Development in Java",
    description:
      "Frontend to backend to deployment, all in one track. Java, Spring Boot and React come together in real projects you can put straight on your portfolio.",
    rating: 4.8,
    reviews: 1120,
    language: "Telugu",
    startDate: "Soon",
    price: 5999,
    paymentButtonId: null,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "java-backend",
        label: "Java & Spring Backend",
        color: GREEN,
        phases: [
          "Core Java — OOP, Collections & Exceptions",
          "JDBC & SQL",
          "Spring Boot & REST APIs",
          "Spring Data JPA & Security",
        ],
      },
      {
        key: "frontend",
        label: "Frontend Development",
        color: CORAL,
        phases: ["HTML, CSS & JavaScript", "React — Components, Hooks & Routing", "Connecting React to APIs"],
      },
      {
        key: "projects",
        label: "Projects & Deployment",
        color: GOLD,
        phases: ["Full Stack Capstone Project", "Git, Docker & Deployment"],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "fullstack-python",
    badge: "🐍 Full Stack",
    icon: FaPython,
    image: pythonfe,
    title: "Full Stack Development in Python",
    description:
      "Python and FastAPI on the backend, React on the front. Learn to design, build and ship complete web apps end to end.",
    rating: 4.7,
    reviews: 648,
    language: "Telugu",
    startDate: "Soon",
    price: 6999,
    paymentButtonId: null,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "python-backend",
        label: "Python & FastAPI Backend",
        color: GREEN,
        phases: [
          "Python Fundamentals & OOP",
          "FastAPI & Pydantic",
          "Databases with SQLAlchemy",
          "Authentication (JWT/OAuth2)",
        ],
      },
      {
        key: "frontend",
        label: "Frontend Development",
        color: CORAL,
        phases: ["HTML, CSS & JavaScript", "React — Components, Hooks & Routing", "Connecting React to APIs"],
      },
      {
        key: "projects",
        label: "Projects & Deployment",
        color: GOLD,
        phases: ["Full Stack Capstone Project", "Git, Docker & Deployment"],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "frontend",
    badge: "🎨 Frontend",
    icon: Monitor,
    image: frontend,
    gradient: ["#C0392B", "#E67E5A"],
    title: "Frontend Development Course",
    description:
      "HTML, CSS, JavaScript and React — build fast, responsive, good-looking interfaces and a portfolio of real projects to back it up.",
    rating: 4.6,
    reviews: 521,
    language: "Telugu",
    startDate: "Soon",
    price: 2999,
    paymentButtonId: null,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "web-basics",
        label: "Web Foundations",
        color: GREEN,
        phases: ["HTML5 & Semantic Markup", "CSS3, Flexbox & Grid", "Responsive Design", "JavaScript (ES6+)"],
      },
      {
        key: "react",
        label: "React & Modern Frontend",
        color: CORAL,
        phases: ["React Components & Hooks", "State Management", "Routing & API Integration", "Frontend Capstone Project"],
      },
      CAREER_TRACK,
    ],
  },
  {
    id: "sap-sd",
    badge: "🏢 Enterprise",
    icon: Briefcase,
    image: sapsd,
    gradient: ["#15140F", "#3A3A2E"],
    title: "SAP SD Course",
    description:
      "Learn SAP Sales & Distribution the way consultants use it — order-to-cash, pricing, billing and the configuration skills enterprises hire for.",
    rating: 4.6,
    reviews: 298,
    language: "Telugu",
    startDate: "Soon",
    price: 1999,
    paymentButtonId: null,
    curriculumLink: null,
    bundleNote: null,
    curriculumTracks: [
      {
        key: "sap-core",
        label: "SAP SD Foundations",
        color: GREEN,
        phases: ["SAP Navigation & Enterprise Structure", "Master Data — Customer & Material", "Order-to-Cash Cycle"],
      },
      {
        key: "sap-config",
        label: "Configuration & Processes",
        color: CORAL,
        phases: ["Pricing Procedures", "Delivery & Shipping", "Billing & Invoicing", "Credit Management"],
      },
      CAREER_TRACK,
    ],
  },
];

const FEATURED_COURSES = COURSES.filter((c) => c.featured);
const REGULAR_COURSES = COURSES.filter((c) => !c.featured);

/* ─── Star rating ─────────────────────────────────────────────────────── */
function Stars({ value, reviews, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={13}
          fill={i < Math.round(value) ? GOLD : "none"}
          stroke={GOLD}
          strokeWidth={1.6}
        />
      ))}
      <span style={{ fontSize: 12.5, fontWeight: 700, color: t.text, marginLeft: 3 }}>{value}</span>
      {reviews ? (
        <span style={{ fontSize: 11.5, color: t.muted }}>({reviews.toLocaleString("en-IN")} reviews)</span>
      ) : null}
    </div>
  );
}

/* ─── Curriculum accordion ────────────────────────────────────────────── */
function CurriculumAccordion({ isDark, tracks }) {
  const t = useTheme(isDark);
  const rowBg = isDark ? "rgba(255,255,255,0.035)" : "#F8F5EB";
  return (
    <Accordion allowToggle defaultIndex={[0]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {tracks.map((track) => (
          <AccordionItem key={track.key} border={`1.5px solid ${t.border}`} borderRadius="14px" overflow="hidden">
            <AccordionButton _hover={{ bg: rowBg }} py={3.5} px={4}>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, textAlign: "left", minWidth: 0 }}>
                <MapPin size={14} color={track.color} strokeWidth={2.4} />
                <span style={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 700, color: t.text }}>{track.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: t.faint }}>[{track.phases.length} modules]</span>
              </div>
              <AccordionIcon color={t.muted} />
            </AccordionButton>
            <AccordionPanel pb={4} px={4} bg={rowBg}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
                {track.phases.map((p) => (
                  <div key={p} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircle2 size={15} color={track.color} strokeWidth={2.4} />
                    <span style={{ fontFamily: BODY, fontSize: 13, fontWeight: 500, color: t.text }}>{p}</span>
                  </div>
                ))}
              </div>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </div>
    </Accordion>
  );
}

/* ─── Curriculum modal trigger + modal ────────────────────────────────── */
function CurriculumModal({ isDark, courseTitle, tracks, curriculumLink }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const t = useTheme(isDark);
  const totalModules = tracks.reduce((sum, tr) => sum + tr.phases.length, 0);

  return (
    <>
      <button
        onClick={onOpen}
        className="glc-curriculum-trigger"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          width: "100%", cursor: "pointer",
          background: isDark ? "rgba(15,76,58,0.16)" : "rgba(15,76,58,0.06)",
          border: `1.5px dashed ${isDark ? "rgba(29,140,103,0.4)" : "rgba(15,76,58,0.28)"}`,
          borderRadius: 12, padding: "11px 14px",
          fontFamily: DISPLAY, fontWeight: 700, fontSize: 13, color: GREEN_LT,
        }}
      >
        <ListChecks size={15} strokeWidth={2.1} />
        View Curriculum
        <ArrowRight size={13} strokeWidth={2.4} />
      </button>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg={t.surfaceSolid} borderRadius={{ base: 0, md: "20px" }}>
          <ModalHeader fontFamily={DISPLAY} fontWeight={800} color={t.text}>
            {courseTitle} — Full Curriculum
          </ModalHeader>
          <ModalCloseButton color={t.muted} />
          <ModalBody pb={6}>
            <p style={{ fontFamily: BODY, fontSize: 13, color: t.muted, margin: "0 0 16px", lineHeight: 1.7 }}>
              {tracks.length} track{tracks.length > 1 ? "s" : ""} · {totalModules} modules — the full plan, end to end.
            </p>
            <CurriculumAccordion isDark={isDark} tracks={tracks} />
          </ModalBody>
          {curriculumLink && (
            <ModalFooter>
              <a
                href={curriculumLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
                  background: GREEN, color: "#fff", fontFamily: DISPLAY, fontWeight: 700, fontSize: 13,
                  padding: "10px 18px", borderRadius: 999,
                }}
              >
                <Download size={14} strokeWidth={2.4} /> Download full PDF <ExternalLink size={13} strokeWidth={2.4} />
              </a>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

/* ─── Sold-out enroll block (kept for future batches) ─────────────────── */
function SoldOutNotice({ isDark, t }) {
  return (
    <div
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: "100%",
        background: isDark ? "rgba(192,57,43,0.14)" : "rgba(192,57,43,0.07)",
        border: `1.5px solid ${isDark ? "rgba(192,57,43,0.4)" : "rgba(192,57,43,0.28)"}`,
        borderRadius: 12, padding: "12px 14px", cursor: "not-allowed",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Lock size={14} strokeWidth={2.3} color={CORAL} />
        <span style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 13, color: CORAL }}>Enrollment Full</span>
      </div>
      <p style={{ fontSize: 11, color: t.muted, margin: 0, textAlign: "center" }}>
        This batch has hit its seat cap — check back for the next cohort.
      </p>
    </div>
  );
}

/* ─── Disabled "Enroll Now" button ────────────────────────────────────── */
function DisabledEnrollButton({ isDark, t }) {
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        width: "100%", cursor: "not-allowed",
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(21,20,15,0.06)",
        border: `1.5px solid ${t.border}`,
        borderRadius: 12, padding: "12px 14px",
        fontFamily: DISPLAY, fontWeight: 700, fontSize: 14, color: t.faint,
        opacity: 0.85,
      }}
    >
      <Lock size={14} strokeWidth={2.3} />
      Enroll Now
    </button>
  );
}

/* ─── Google Form enroll button ───────────────────────────────────────── */
function FormEnrollButton({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="glc-form-enroll"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        width: "100%", textDecoration: "none", cursor: "pointer",
        background: GREEN_LT, color: "#fff",
        borderRadius: 12, padding: "13px 14px",
        fontFamily: DISPLAY, fontWeight: 800, fontSize: 14,
      }}
    >
      {label} <ExternalLink size={14} strokeWidth={2.4} />
    </a>
  );
}

/* ─── Hero banner (image kept; chips are Gen-Z flavoured) ─────────────── */
function HeroBanner({ isDark }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{ marginBottom: 32 }}
    >
      <div
        style={{
          position: "relative", borderRadius: 22, overflow: "hidden",
          border: `2px solid ${isDark ? "rgba(255,209,102,0.35)" : "rgba(15,76,58,0.18)"}`,
          boxShadow: isDark ? "0 24px 70px rgba(0,0,0,0.5)" : "0 20px 50px rgba(15,20,15,0.14)",
        }}
      >
        <img
          src={heroBanner}
          alt="GrapplTech God Level Courses"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </div>

      <div
        style={{
          display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center",
          gap: "8px 14px", marginTop: 14,
        }}
      >
        {[
          { icon: Flame, label: "Fresh drops are live" },
          { icon: Sparkles, label: "No cap, real curriculum" },
          { icon: Zap, label: "Telugu-first, built different" },
        ].map(({ icon: I, label }) => (
          <span
            key={label}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, color: GREEN_LT,
              background: isDark ? "rgba(15,76,58,0.2)" : "rgba(15,76,58,0.07)",
              border: `1px solid ${isDark ? "rgba(29,140,103,0.35)" : "rgba(15,76,58,0.2)"}`,
              borderRadius: 999, padding: "6px 12px", textTransform: "uppercase", letterSpacing: "0.03em",
            }}
          >
            <I size={12} strokeWidth={2.4} />
            {label}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── Special "Fresh Drops" header + AI-era callouts ──────────────────── */
function FreshDropsHeader({ isDark, t }) {
  const calloutStyle = {
    display: "flex", alignItems: "flex-start", gap: 14, marginTop: 16,
    background: isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.75)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(21,20,15,0.12)"}`,
    borderRadius: 16, padding: "16px 18px",
  };
  const iconBoxStyle = {
    width: 38, height: 38, borderRadius: 11, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: GREEN_LT + "26", color: GREEN_LT,
  };

  return (
    <Box mb={{ base: 8, md: 10 }}>
      <div
        style={{
          position: "relative", overflow: "hidden", borderRadius: 24, padding: "clamp(22px, 4vw, 40px)",
          background: isDark
            ? "linear-gradient(135deg, rgba(15,76,58,0.55), rgba(21,20,15,0.9))"
            : "linear-gradient(135deg, rgba(15,76,58,0.10), rgba(252,3,4,0.05))",
          border: `1.5px solid ${isDark ? "rgba(29,140,103,0.45)" : "rgba(15,76,58,0.28)"}`,
          boxShadow: isDark ? "0 24px 70px rgba(0,0,0,0.45)" : "0 18px 46px rgba(15,76,58,0.12)",
        }}
      >
        <span
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14,
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
            color: "#fff", background: CORAL, borderRadius: 999, padding: "6px 12px",
          }}
        >
          <Flame size={12} strokeWidth={2.6} /> Fresh drops · enrollment live
        </span>
        <Heading fontFamily={DISPLAY} size="xl" mb={3} fontWeight="800" color={t.text} letterSpacing="-0.02em">
          Fresh batches just dropped. Don't be an NPC. 🚀
        </Heading>
        <Text fontFamily={BODY} fontSize="lg" color={t.muted} maxW="3xl">
          A free live Python course to kickstart your journey, Data Engineering and AI Engineering
          for anyone who wants to actually win the AI era, the Falcon combo for the ones who want
          both, and Spring Boot for the backend girlies and gigachads. Seats are open, the vibes are
          immaculate, the glow-up is real.
        </Text>

        {/* Falcon / 2027 glow-up callout */}
        <div style={calloutStyle}>
          <span style={iconBoxStyle}>
            <Rocket size={19} strokeWidth={2.2} />
          </span>
          <div>
            <p style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 16, color: t.text, margin: "0 0 4px" }}>
              Dec 31 is your deadline. 2027 is your main character arc. 🦅
            </p>
            <p style={{ fontFamily: BODY, fontSize: 13.5, color: t.muted, margin: 0, lineHeight: 1.65 }}>
              Real talk: by the end of December 31, you could be an AI Engineer AND a Data Engineer.
              Two skills, two months, one glow-up. While everyone else is posting "new year, new me"
              and doing absolutely nothing, you'll walk into 2027 with the receipts — real projects,
              real pipelines, real AI apps — as a whole new person. Start Nov 1, ship by Dec 31,
              flex in 2027. No cap. 🔥
            </p>
          </div>
        </div>

        {/* AI-era callout */}
        <div style={calloutStyle}>
          <span style={iconBoxStyle}>
            <Bot size={19} strokeWidth={2.2} />
          </span>
          <div>
            <p style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 16, color: t.text, margin: "0 0 4px" }}>
              Data Engineering is the best bet in the AI era. Period. 🏆
            </p>
            <p style={{ fontFamily: BODY, fontSize: 13.5, color: t.muted, margin: 0, lineHeight: 1.65 }}>
              Every AI model runs on data, and every dataset needs someone to collect, clean and ship it.
              While everyone else is prompting, data engineers are building the pipes that make AI work.
              Low competition, high demand, zero fluff — this is the skill that ages well.
            </p>
          </div>
        </div>
      </div>
    </Box>
  );
}

/* ─── Course card ─────────────────────────────────────────────────────── */
function CourseCard({ course, isDark }) {
  const t = useTheme(isDark);
  const hasOriginal = !!course.originalPrice;
  const disc = hasOriginal ? Math.round((1 - course.price / course.originalPrice) * 100) : 0;
  const isFree = course.price === 0;
  const Icon = course.icon;
  const isSoldOut = !!course.soldOut;
  const isOpen = ENROLLMENT_OPEN || !!course.enrollOpen;
  const canEnrollForm = isOpen && !!course.enrollLink;
  const canEnrollRazorpay = isOpen && !course.enrollLink && !!course.paymentButtonId;
  const canEnroll = canEnrollForm || canEnrollRazorpay;
  const spotlight = !!course.spotlight;
  /* fullImage: show the whole picture (no crop, no zoom, no dark overlay) */
  const showFull = !!course.fullImage && !!course.image;

  return (
    <motion.div
      role="group"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className="glc-card"
      style={{
        background: t.cardBg,
        border: spotlight ? `2px solid ${GREEN_LT}` : `1px solid ${t.border}`,
        borderRadius: 24,
        padding: 8,
        boxShadow: spotlight
          ? `0 0 0 4px ${GREEN_LT}22, ${isDark ? "0 20px 60px rgba(0,0,0,0.4)" : "0 16px 40px rgba(15,76,58,0.16)"}`
          : isDark ? "0 20px 60px rgba(0,0,0,0.4)" : "0 16px 40px rgba(15,20,15,0.08)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* image (or gradient placeholder) + badge */}
      <div className="glc-card-image" style={{ position: "relative", height: showFull ? "auto" : 190, overflow: "hidden", borderRadius: 18, background: showFull ? (isDark ? "#15140F" : "#F3EDDC") : undefined }}>
        <Badge
          position="absolute" top={4} right={4} zIndex={2}
          bg={isSoldOut ? CORAL : spotlight ? GREEN_LT : (isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.92)")}
          color={isSoldOut || spotlight ? "#fff" : (isDark ? "#fff" : INK)}
          borderRadius="full" px={3} py={1}
          fontFamily={MONO} fontWeight={700} fontSize="10px" letterSpacing="0.04em" textTransform="uppercase"
        >
          {isSoldOut ? "Batch Full" : course.badge}
        </Badge>
        {course.image ? (
          <img
            src={course.image}
            alt={course.title}
            className={showFull ? "" : "glc-card-img"}
            style={{
              width: "100%",
              height: showFull ? "auto" : "100%",
              objectFit: showFull ? "contain" : "cover",
              display: "block",
              filter: isSoldOut ? "grayscale(0.55)" : "none",
            }}
          />
        ) : (
          <div
            className="glc-card-img"
            role="img"
            aria-label={course.title}
            style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${course.gradient[0]}, ${course.gradient[1]})`,
            }}
          >
            <Icon size={64} color="rgba(255,255,255,0.9)" strokeWidth={1.6} />
          </div>
        )}
        {!showFull && (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent 55%)" }} />
        )}
      </div>

      <div style={{ padding: "20px 18px 22px", display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
        {/* icon + title */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDark ? "rgba(15,76,58,0.22)" : "rgba(15,76,58,0.08)", color: GREEN_LT,
            }}>
              <Icon size={16} />
            </span>
            <h3 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 17, color: t.text, margin: 0, lineHeight: 1.25 }}>
              {course.title}
            </h3>
          </div>
          {course.rating ? <Stars value={course.rating} reviews={course.reviews} t={t} /> : null}
        </div>

        <p style={{ fontFamily: BODY, fontSize: 13, color: t.muted, margin: 0, lineHeight: 1.65 }}>
          {course.description}
        </p>

        {/* meta: duration / start date / end date / mode / seats / language */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
          {course.duration && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
              <Clock size={13} strokeWidth={2.2} color={GREEN_LT} /> {course.duration}
            </span>
          )}
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
            <Calendar size={13} strokeWidth={2.2} color={GREEN_LT} /> Starts <strong style={{ color: t.text }}>{course.startDate}</strong>
          </span>
          {course.endDate && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
              <CalendarCheck size={13} strokeWidth={2.2} color={GREEN_LT} /> Ends <strong style={{ color: t.text }}>{course.endDate}</strong>
            </span>
          )}
          {course.mode && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
              <Globe size={13} strokeWidth={2.2} color={GREEN_LT} /> {course.mode}
            </span>
          )}
          {course.seats && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
              <Users size={13} strokeWidth={2.2} color={GREEN_LT} /> <strong style={{ color: t.text }}>{course.seats}</strong> seats
            </span>
          )}
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: t.muted }}>
            <Languages size={13} strokeWidth={2.2} color={GREEN_LT} /> {course.language}
          </span>
        </div>

        {/* bundle note — combo / cross-sell callouts */}
        {course.bundleNote && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            background: isDark ? "rgba(252,3,4,0.10)" : "rgba(252,3,4,0.06)",
            border: `1px solid ${isDark ? "rgba(252,3,4,0.35)" : "rgba(252,3,4,0.22)"}`,
            borderRadius: 12, padding: "9px 12px",
          }}>
            <Gift size={14} strokeWidth={2.2} color={GOLD} style={{ marginTop: 1, flexShrink: 0 }} />
            <p style={{ fontFamily: BODY, fontSize: 11.5, fontWeight: 600, color: t.text, margin: 0, lineHeight: 1.5 }}>
              {course.bundleNote}
            </p>
          </div>
        )}

        {/* price strip */}
        <div style={{
          display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
          background: isDark ? "rgba(15,76,58,0.12)" : "rgba(15,76,58,0.05)",
          border: `1px solid ${isDark ? "rgba(29,140,103,0.28)" : "rgba(15,76,58,0.16)"}`,
          borderRadius: 12, padding: "10px 14px",
          opacity: isSoldOut ? 0.6 : 1,
        }}>
          <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 22, color: GREEN_LT }}>
            {isFree ? "Free" : `₹${course.price.toLocaleString("en-IN")}`}
          </span>
          {hasOriginal && (
            <>
              <span style={{ fontFamily: BODY, fontSize: 12.5, color: t.faint, textDecoration: "line-through" }}>
                ₹{course.originalPrice.toLocaleString("en-IN")}
              </span>
              <span style={{
                marginLeft: "auto", fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
                color: CORAL, background: isDark ? "rgba(192,57,43,0.14)" : "rgba(192,57,43,0.08)",
                border: `1px solid ${isDark ? "rgba(192,57,43,0.35)" : "rgba(192,57,43,0.22)"}`,
                padding: "3px 9px", borderRadius: 999,
              }}>
                {disc}% off
              </span>
            </>
          )}
          {isFree && course.seats && (
            <span style={{
              marginLeft: "auto", fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
              color: CORAL, background: isDark ? "rgba(192,57,43,0.14)" : "rgba(192,57,43,0.08)",
              border: `1px solid ${isDark ? "rgba(192,57,43,0.35)" : "rgba(192,57,43,0.22)"}`,
              padding: "3px 9px", borderRadius: 999,
            }}>
              Limited seats
            </span>
          )}
        </div>

        {/* actions — curriculum modal + enroll area */}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          <CurriculumModal
            isDark={isDark}
            courseTitle={course.title}
            tracks={course.curriculumTracks}
            curriculumLink={course.curriculumLink}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {isSoldOut ? (
              <SoldOutNotice isDark={isDark} t={t} />
            ) : !canEnroll ? (
              <>
                <DisabledEnrollButton isDark={isDark} t={t} />
                <p style={{ fontSize: 10.5, color: t.muted, margin: 0, textAlign: "center" }}>
                  Enrollment is currently closed
                </p>
              </>
            ) : canEnrollForm ? (
              <>
                <FormEnrollButton
                  href={course.enrollLink}
                  label={isFree ? "Enroll for Free" : "Enroll Now"}
                />
                <p style={{ fontSize: 10.5, color: t.muted, margin: 0, textAlign: "center" }}>
                  Register via Google Form · only {course.seats ?? "limited"} seats
                </p>
              </>
            ) : (
              <>
                <RazorpayPaymentButton buttonId={course.paymentButtonId} />
                <p style={{ fontSize: 10.5, color: t.muted, margin: 0, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                  <ShieldCheck size={11} strokeWidth={2.2} /> Secured by Razorpay
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Main export ────────────────────────────────────────────────────── */
export default function GodLevelCoursesBootcamp() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const t = useTheme(isDark);

  return (
    <>
      <style>{`
        ${FONT_LINK}
        .glc-page { font-family: ${BODY}; -webkit-font-smoothing: antialiased; box-sizing: border-box; overflow-x: hidden; }
        .glc-page *, .glc-page *::before, .glc-page *::after { box-sizing: border-box; }

        .glc-card-image { transition: transform 0.2s ease; }
        .glc-card-img { transition: transform 0.5s ease; }
        .glc-card:hover .glc-card-img { transform: scale(1.08); }

        .glc-curriculum-trigger { transition: transform 0.15s ease; border: none; }
        .glc-curriculum-trigger:hover { transform: translateY(-1px); }

        .glc-form-enroll { transition: transform 0.15s ease, filter 0.15s ease; }
        .glc-form-enroll:hover { transform: translateY(-1px); filter: brightness(1.08); }

        .glc-rzp-form { display: flex; justify-content: center; width: 100%; max-width: 100%; overflow: hidden; }

        img { max-width: 100%; }

        @media (max-width: 767px) {
          .glc-page { padding: 0 !important; }
        }
      `}</style>

      <Box className="glc-page" py={{ base: 12, md: 20 }} bg={t.bg}>
        <Container maxW="7xl" px={{ base: 4, sm: 5, md: 8 }}>
          <HeroBanner isDark={isDark} />

          {/* ── Fresh drops: live enrollment ── */}
          <FreshDropsHeader isDark={isDark} t={t} />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 8 }} mb={{ base: 14, md: 20 }}>
            {FEATURED_COURSES.map((course) => (
              <CourseCard key={course.id} course={course} isDark={isDark} />
            ))}
          </SimpleGrid>

          {/* ── Everything else ── */}
          <Box mb={{ base: 10, md: 14 }} textAlign="left">
            <Badge
              bg={isDark ? `${GREEN}2A` : `${GREEN}0C`}
              color={GREEN_LT}
              border={`1px solid ${isDark ? `${GREEN}45` : `${GREEN}25`}`}
              variant="subtle" px={3} py={1} borderRadius="full" mb={4}
              fontFamily={MONO} fontWeight={700} letterSpacing="0.04em" textTransform="uppercase"
            >
              God Level Courses · The Full Lineup
            </Badge>
            <Heading fontFamily={DISPLAY} size="2xl" mb={4} fontWeight="800" color={t.text} letterSpacing="-0.02em">
              Level up. Get hired. Main character era. ✨
            </Heading>
            <Text fontFamily={BODY} fontSize="xl" color={t.muted} maxW="2xl">
              Backend, Full Stack, Frontend, C++, SAP and Low Level Design — every God Level bootcamp
              in Telugu, built to turn "I'm still learning" into "I'm hired." No cap, no filler,
              just skills that actually slap.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 10 }}>
            {REGULAR_COURSES.map((course) => (
              <CourseCard key={course.id} course={course} isDark={isDark} />
            ))}
          </SimpleGrid>

          <div style={{
            display: "flex", alignItems: "center", gap: 12, marginTop: 40,
            background: isDark ? "rgba(15,76,58,0.14)" : "rgba(15,76,58,0.06)",
            border: `1.5px solid ${isDark ? "rgba(29,140,103,0.32)" : "rgba(15,76,58,0.22)"}`,
            borderRadius: 14, padding: "13px 16px",
          }}>
            <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: GREEN_LT + "22", color: GREEN_LT }}>
              <Wallet size={16} strokeWidth={2.2} />
            </span>
            <p style={{ fontSize: 13, color: t.text, margin: 0, lineHeight: 1.55 }}>
              <strong>One-time payment. Lifetime access to recordings.</strong>{" "}
              <span style={{ color: t.muted }}>
                No hidden charges, no subscription traps, no plot twists. Lock in your seat and thank
                yourself later. 💸
              </span>
            </p>
          </div>
        </Container>
      </Box>
    </>
  );
}